import { VisibilityOffRounded } from "@mui/icons-material";
import { Button, Link, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import {
  CreateIssueModal,
  IssueStatusPicker,
  isWhitespace,
  ItemProperty,
  MaterialSymbolIcon,
  MaterialSymbols,
} from "..";
import { DEFAULT_ISSUE_STATUS, IssuePrivacyType, Links, SupportingItemType } from "../../constants";
import { SupportingIssueDto } from "../../http";

const LOADING_SKELETON_WIDTHS = [110, 180, 145];

interface IProps {
  items: SupportingIssueDto[];
  context?: {
    teamName: string;
    teamSlug: string;
    actionItemGuid: string;
    actionItemDescription: string;
  };
  isLoading?: boolean;
  onClick?: (type: SupportingItemType, issueItemGuid: string) => void;
}

const SupportingIssuesProperty = (props: IProps) => {
  const [isCreateIssueModalVisible, setIsCreateIssueModalVisible] = useState(false);
  const theme = useTheme();

  function handleClicked(event: React.MouseEvent, issueItemGuid: string) {
    if (props.onClick) {
      event.preventDefault();
      props.onClick(SupportingItemType.Issue, issueItemGuid);
    }
  }

  return (
    <ItemProperty labelIcon={<MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} />} label="Loops">
      <Stack spacing={0.5}>
        {props.isLoading ? (
          <Stack spacing={0.5}>
            {LOADING_SKELETON_WIDTHS.map((width, i) => (
              <Skeleton key={i} variant="rectangular" height={24} width={width} />
            ))}
          </Stack>
        ) : (
          props.items.map((issue) => (
            <Stack direction="row" key={issue.issueGuid} sx={{
              alignItems: "center"
            }}>
              <IssueStatusPicker
                issueGuid={issue.issueGuid}
                status={issue.currentStatus ?? DEFAULT_ISSUE_STATUS}
                hasDecision={issue.hasDecision}
                createdByUserGuid={issue.createdByUserId}
                teamSlug={issue.team.slug}
                variant="button"
                disabled={true}
              />
              <Stack direction="row" spacing={0.25} sx={{
                alignItems: "center"
              }}>
                <Tooltip
                  title={
                    <Typography>
                      {issue.team && (
                        <>
                          <strong>{issue.team.name}</strong>
                          <span> : </span>
                        </>
                      )}
                      <Link href={Links.IssueDetail(issue.issueGuid)}>{issue.description}</Link>
                    </Typography>
                  }
                  placement="right"
                >
                  <Link href={Links.IssueDetail(issue.issueGuid)} onClick={(e) => handleClicked(e, issue.issueGuid)}>
                    {issue.description == null || isWhitespace(issue.description) ? (
                      <Typography variant="placeholder">Nothing here yet.</Typography>
                    ) : (
                      <Typography>{issue.description}</Typography>
                    )}
                  </Link>
                </Tooltip>

                {issue.privacy === IssuePrivacyType.Limited && (
                  <Tooltip
                    title={
                      <Stack>
                        <Typography>Limited</Typography>
                        <Typography variant="caption" sx={{ lineHeight: "1rem" }}>
                          Visible only to creator, decision maker, and assigned team members.
                        </Typography>
                      </Stack>
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          color: theme.palette.error.main,
                        },
                      },
                    }}
                  >
                    <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          ))
        )}

        <Button
          variant="tertiary"
          startIcon={<MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} />}
          sx={{ alignSelf: "start", whiteSpace: "nowrap" }}
          onClick={() => setIsCreateIssueModalVisible(true)}
        >
          Add
        </Button>
      </Stack>

      <CreateIssueModal
        visible={isCreateIssueModalVisible}
        context={props.context}
        onCreated={() => setIsCreateIssueModalVisible(false)}
        onCancelled={() => setIsCreateIssueModalVisible(false)}
      />
    </ItemProperty>
  );
};

export { SupportingIssuesProperty };
